import { BookConstants } from '@/constants/bookConstants';
import type { DisplayMode } from '@/types/book';

export type PageCornerSide = 'left' | 'right';

const { THICKNESS_LINE_WIDTH } = BookConstants;

/** 将书页圆角限制在合法的页面逻辑尺寸范围内。 */
export function normalizePageRadius(
  radius: number | undefined,
  pageWidth: number,
  pageHeight: number
): number {
  if (!Number.isFinite(radius) || radius === undefined || radius <= 0) return 0;

  return Math.min(radius, Math.max(0, Math.min(pageWidth, pageHeight) / 2));
}

/** 为厚度线预留完整的页面圆角绘制宽度（线区宽度 = 线数 × 固定线宽）。 */
export function resolveThicknessCornerGeometry(
  radius: number | undefined,
  pageWidth: number,
  pageHeight: number,
  lineCount: number
): { radius: number; radiusExtent: number; textureWidth: number } {
  const normalizedRadius = normalizePageRadius(radius, pageWidth, pageHeight);
  const radiusExtent = Math.ceil(normalizedRadius);
  const linesWidth = Math.max(0, lineCount) * THICKNESS_LINE_WIDTH;
  return {
    radius: normalizedRadius,
    radiusExtent,
    textureWidth: linesWidth + radiusExtent
  };
}

interface ThicknessLineGeometryInput {
  radius: number;
  radiusExtent: number;
  lineOffset: number;
  lineCount: number;
  maxLineCount: number;
  pageHeight: number;
  side: PageCornerSide;
}

/** 计算厚度线横向位置：每条线占固定 THICKNESS_LINE_WIDTH，中心落在半像素上。 */
export function resolveThicknessLineGeometry(input: ThicknessLineGeometryInput): {
  lineX: number;
  inset: number;
} {
  const linePitch = THICKNESS_LINE_WIDTH;
  const lineX =
    (input.side === 'right' ? input.radiusExtent : 0) +
    input.lineOffset * linePitch +
    linePitch / 2;
  if (input.radius > 0 || input.lineCount <= 1) return { lineX, inset: 0 };

  const lineIndex =
    input.side === 'left' ? input.lineCount - 1 - input.lineOffset : input.lineOffset;
  const progress = Math.max(0, Math.min(1, lineIndex / (input.lineCount - 1)));
  const thicknessRatio = Math.max(
    0,
    Math.min(1, input.lineCount / Math.max(1, input.maxLineCount))
  );

  return {
    lineX,
    inset: progress * input.pageHeight * 0.02 * thicknessRatio
  };
}

/** 根据展示模式和页码确定仅作用于书本外侧的圆角方向。 */
export function resolvePageCornerSide(index: number, displayMode: DisplayMode): PageCornerSide {
  if (displayMode === 'single') return 'right';
  return index % 2 === 0 ? 'right' : 'left';
}

/** 页面背面在临时翻页副本中会水平镜像，因此外侧圆角也需要交换方向。 */
export function resolvePageFaceCornerSide(
  side: PageCornerSide,
  face: 'front' | 'back'
): PageCornerSide {
  if (face === 'front') return side;
  return side === 'left' ? 'right' : 'left';
}
